// A hub's pages as tabs, in the top bar's left half (CustomerHeader): on
// /projects "Projects · Deliverables", on /workflows "Workflows · Runs ·
// Schedules" (Todos and Tasks are rows of their own since 20 Oct 2026). Quiet links: the current one in full colour and
// heavier, with no fill (a filled pill read as one more of the page's own
// segmented filters, "All / Needs you", "By date"; an underline as a second
// tab strip above an agent's Chat, Files …); the others muted, raised on
// hover. Each is a page of its own (a <nav> of links, aria-current on the
// current one, every link in the Tab order), so a tab never changes
// anything but the URL. Which hub and which tab: navModel's hubTabsAt. On
// a page under a tab (a task, a run) the bar shows crumbs instead.
//
// A tab carries the count of what waits behind it, so a hub row's number
// is explained where its pages are: Runs how many runs are live ("2 live",
// from the list the Workflows page polls).

import { Link } from "react-router-dom";

import { useTasks } from "@/api/tasks";
import { isLiveRunStatus, useWorkflowRuns } from "@/api/workflows";
import { COARSE_HIT_AREA, FOCUS_RING_INSET, cx } from "@/components/ui/controlStyles";
import { TabCount, type TabCountTone } from "@/components/ui/Tabs";
import { attentionTone, countPillTone } from "@/features/attention/tones";
import { pluralize } from "@/lib/format";

import type { NavPage, NavRow } from "./navModel";
import { useNavCounts } from "./useNavCounts";

interface TabBadge {
  value: number | string;
  tone: TabCountTone;
  /** Read after the tab's name. */
  label: string;
}

interface HubTabsProps {
  customerId: string;
  hub: { row: NavRow; current: NavPage };
  className?: string;
}

export function HubTabs({ customerId, hub, className }: HubTabsProps) {
  const { row, current } = hub;
  const has = (key: string) => row.pages.some((p) => p.key === key);
  const live = useLiveRuns(has("runs") ? customerId : null);
  const tasksNeedYou = useTasks(has("tasks") ? customerId : null, { limit: 1 }).data?.counts?.needs_you ?? 0;
  const todos = useNavCounts(has("todos") ? customerId : null).todos;
  const badge = (key: string): TabBadge | undefined => {
    if (key === "runs" && live > 0) return { value: `${live} live`, tone: "attention", label: `${live} live` };
    if (key === "todos" && todos) return { value: todos.value > 99 ? "99+" : todos.value, tone: todos.tone, label: todos.label };
    if (key === "tasks" && tasksNeedYou > 0) return { value: tasksNeedYou, tone: countPillTone(attentionTone("tasks")), label: `${pluralize(tasksNeedYou, "task")} ${tasksNeedYou === 1 ? "needs" : "need"} you` };
    return undefined;
  };
  return (
    <nav aria-label={row.label} className={cx("flex min-w-0 items-center gap-1 overflow-x-auto scrollbar-none", className)}>
      {row.pages.map((p) => {
        const on = p.key === current.key;
        const count = badge(p.key);
        return (
          <Link
            key={p.key}
            to={p.to}
            aria-current={on ? "page" : undefined}
            title={count ? `${p.tab ?? p.label} (${count.label})` : undefined}
            className={cx(
              "relative inline-flex h-[32px] shrink-0 items-center gap-2 rounded-control px-3 text-base transition-colors",
              FOCUS_RING_INSET,
              COARSE_HIT_AREA,
              on ? "font-semibold text-fg" : "font-medium text-fg-muted [@media(hover:hover)]:hover:bg-raised [@media(hover:hover)]:hover:text-fg",
            )}
          >
            {p.tab ?? p.label}
            {count && (
              <>
                <span aria-hidden className="inline-flex">
                  <TabCount count={count.value} tone={count.tone} />
                </span>
                <span className="sr-only">, {count.label}</span>
              </>
            )}
          </Link>
        );
      })}
    </nav>
  );
}

/** Runs that are live, or 0 (also while the list loads or when it is not wanted). */
function useLiveRuns(customerId: string | null): number {
  const runs = useWorkflowRuns(customerId);
  return (runs.data?.runs ?? []).filter((r) => isLiveRunStatus(r.status)).length;
}
